import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Group as GroupIcon,
  Add as AddGroupIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
  Key as KeyIcon,
  ContentCopy as CopyIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Refresh as RestartIcon,
  Settings as PipelineIcon,
  Dashboard as ControlPanelIcon,
  AutoFixHigh as WizardIcon,
  BugReport as BugReportIcon,
  Link as LinkIcon,
  Sync as ConvergenceIcon,
  Assessment as ProofPackIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';
import { api, endpoints, ensureUiScope } from '../services/api';
import { scheduleDeferredWork } from '../utils/deferredWork';
import { CONFIG_SCOPE_LABELS } from '../utils/configVocabulary';
import { readAcceptanceProofCache } from '../utils/acceptanceArtifacts';
import { resolveSafetyReportOpenPath } from '../utils/safetyReportOpenNavigation';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchOpsRunbookTemplates,
  executeRunbookServer,
  type OpsRunbookTemplate,
} from '../services/opsCopilot';
import { FirmwareReadinessPanel } from '../components/FirmwareReadinessPanel';
import { PlaybookCallout, buildDocsUrl } from '../components/PlaybookCallout';
import { getConfigDrift, type ConfigDriftResponse } from '../services/configDrift';
import { buildArtifactFlowPath } from '../utils/reportFlow';
import { getDeviceLifecycleMetadata } from '../utils/statusMetadata';
import { sitesApi, type Site, type SiteArea } from '../services/sitesApi';
import { resolveDeviceSiteDisplay, resolveEdgePlacementDisplay } from '../utils/locationDisplay';
import { mergeProvisioningStatusIntoDevices } from '../utils/mergeProvisioningStatus';
import { hasCapability } from '../auth/access';
import { DevicePlacementFields } from '../components/location/DevicePlacementFields';
import { DeviceSiteAreaCell } from '../components/location/DeviceSiteAreaCell';
import {
  buildDeviceUpdatePayload,
  edgeLabelFromForm,
  saveDevicePlacement,
} from '../components/location/devicePlacementSave';

interface Device {
  id: string;
  device_id: string;
  org_id?: string;
  site_id?: string;
  area_id?: string;
  area_name?: string;
  placement_label?: string;
  name: string;
  description: string;
  device_type: string;
  location: string;
  group_id?: string;
  status: string;
  last_seen: string;
  current_config_version: string;
  capabilities: any;
  device_metadata: any;
  ip_address?: string;
  mac_address?: string;
  // Fast liveness (from cloud /devices enrichment)
  heartbeat_age_s?: number | null;
  edge_alive?: boolean;
  edge_liveness_status?: 'ok' | 'degraded' | 'down' | 'unknown' | string;
  edge_offline_threshold_sec?: number;
  edge_workload_status?: 'ok' | 'degraded' | 'down' | 'unknown' | 'not_configured' | string;
  configured_camera_count?: number;
  pipeline_state?: string | null;
  pipeline_state_age_s?: number | null;
  // Provisioning status (enriched via provisioning status batch endpoint)
  provisioning_status?: string;
  has_pending_token?: boolean;
  token_expires_at?: string | null;
  activated_at?: string | null;
  lifecycle_summary?: {
    state?: string;
    reason?: string;
    provisioning_status?: string | null;
    deployment_status?: string | null;
    deployment_config_type?: string | null;
    deployment_id?: string | null;
    post_deploy_status?: string | null;
    edge_liveness_status?: string | null;
    decommission_phase?: string | null;
  };
}

interface DeviceGroup {
  id: string;
  name: string;
  description: string;
  location: string;
  tags: any;
  device_count: number;
}

interface DeviceFormData {
  device_id: string;
  name: string;
  description: string;
  device_type: string;
  location: string;
  area_id: string;
  group_id: string;
  capabilities: any;
  device_metadata: any;
}

interface GroupFormData {
  name: string;
  description: string;
  location: string;
  tags: any;
}

type RegistrySummary = {
  cameras_count: number;
  pipelines_running: number;
  pipelines_total: number;
  in_sync?: boolean;
  edge_reachable?: boolean;
  updated_at_ms: number;
  error?: string;
  error_at_ms?: number;
  stale?: boolean;
};

const PAGE_TIMEOUT_MS = 10000;

function withTimeout<T>(promise: Promise<T>, label: string, timeoutMs: number = PAGE_TIMEOUT_MS): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }) as Promise<T>;
}

function offlineThresholdSec(): number {
  const configured = Number(process.env.REACT_APP_DEVICE_OFFLINE_THRESHOLD_SEC || 120);
  return Number.isFinite(configured)
    ? Math.max(10, Math.min(24 * 3600, Math.floor(configured)))
    : 120;
}

function deviceLivenessStatus(device: Device): string {
  if (device.edge_liveness_status) return String(device.edge_liveness_status);
  const age = device.heartbeat_age_s;
  if (typeof age !== 'number' || !Number.isFinite(age)) return 'unknown';
  return age <= offlineThresholdSec() ? 'ok' : 'down';
}

function deviceWorkloadStatus(device: Device): string {
  return String(device.edge_workload_status || 'unknown').toLowerCase();
}

function isDeviceOperational(device: Device): boolean {
  const workload = deviceWorkloadStatus(device);
  return deviceLivenessStatus(device) === 'ok'
    && (workload === 'ok' || workload === 'not_configured');
}

const Devices: React.FC = () => {
  const { effectiveUser: user, isLoading: authLoading } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canReadDevices = capabilities.includes('devices:read');
  const canWriteDevices = capabilities.includes('devices:write');
  const canWriteSites = hasCapability(capabilities, 'sites:write');
  const navigate = useNavigate();
  const location = useLocation();
  const grafanaBase = (process.env.REACT_APP_GRAFANA_URL || '').trim().replace(/\/+$/, '');
  const { orgId, siteId } = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || '');
      const qSite = (sp.get('site') || sp.get('site_id') || '').trim();
      const qOrg = (sp.get('org') || sp.get('org_id') || '').trim();
      const scope = ensureUiScope();
      const site = qSite || String(scope?.siteId || '').trim();
      const org = qOrg || String(scope?.orgId || '').trim();
      return { siteId: site, orgId: org };
    } catch {
      return { siteId: '', orgId: '' };
    }
  }, [location.search]);

  const openPersistedProofPack = useCallback(async (fallbackPath: string, cacheDeviceId?: string) => {
    const proofPreview = readAcceptanceProofCache(orgId, siteId, cacheDeviceId);
    const nextPath = await resolveSafetyReportOpenPath({
      fallbackPath,
      persistedReportId: proofPreview?.persisted_report?.report_id,
    });
    navigate(nextPath);
  }, [navigate, orgId, siteId]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [groups, setGroups] = useState<DeviceGroup[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteAreas, setSiteAreas] = useState<SiteArea[]>([]);
  const [_loading, setLoading] = useState(true);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [openDeviceDialog, setOpenDeviceDialog] = useState(false);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [editingGroup, setEditingGroup] = useState<DeviceGroup | null>(null);
  
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenDevice, setTokenDevice] = useState<Device | null>(null);
  const [generatedToken, setGeneratedToken] = useState<{
    token: string;
    device_id?: string;
    device_name?: string;
    expires_at: string;
    valid_minutes?: number;
    valid_hours?: number;
    message?: string;
    cloud_url?: string;
    mqtt_host?: string;
    mqtt_port?: number;
    provisioning_bundle?: string;
    usb_token_file?: any;
  } | null>(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [commandCopied, setCommandCopied] = useState(false);
  const [copyHint, setCopyHint] = useState<string | null>(null);
  const tokenTextRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [claimingToken, setClaimingToken] = useState(false);
  const [claimResult, setClaimResult] = useState<any | null>(null);

  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [claimTokenInput, setClaimTokenInput] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  
  const [pipelineDialogOpen, setPipelineDialogOpen] = useState(false);
  const [pipelineDevice, setPipelineDevice] = useState<Device | null>(null);
  const [selectedPipeline, setSelectedPipeline] = useState<string>('');
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineMessage, setPipelineMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const [firmwareDialogOpen, setFirmwareDialogOpen] = useState(false);
  const [firmwareDevice, setFirmwareDevice] = useState<Device | null>(null);

  const [registrySummaries, setRegistrySummaries] = useState<Record<string, RegistrySummary>>({});

  const [diagnosisSummaries, setDiagnosisSummaries] = useState<Record<string, any>>({});
  const [diagnosisAutoRefresh, setDiagnosisAutoRefresh] = useState<boolean>(true);
  const [diagnosisUpdatedAtMs, setDiagnosisUpdatedAtMs] = useState<number>(0);
  const [diagnosisLoading, setDiagnosisLoading] = useState<boolean>(false);
  const [convergenceSummaries, setConvergenceSummaries] = useState<Record<string, any>>({});
  const [convergenceUpdatedAtMs, setConvergenceUpdatedAtMs] = useState<number>(0);
  const [convergenceLoading, setConvergenceLoading] = useState<boolean>(false);

  const [liveConfigDriftByDevice, setLiveConfigDriftByDevice] = useState<Record<string, ConfigDriftResponse>>({});
  const [liveConfigDriftUpdatedAtMs, setLiveConfigDriftUpdatedAtMs] = useState<number>(0);
  const [liveConfigDriftLoading, setLiveConfigDriftLoading] = useState<boolean>(false);

  const [provisionWatchDeviceId, setProvisionWatchDeviceId] = useState<string | null>(null);
  const [provisionWatchRunning, setProvisionWatchRunning] = useState<boolean>(false);
  const [provisionWatch, setProvisionWatch] = useState<any | null>(null);

  const [siteFilter, setSiteFilter] = useState<string>('all');

  const devicesListFetchGenRef = useRef(0);
  const registrySummaryInFlightRef = useRef<Set<string>>(new Set());
  const diagnosisBatchInFlightRef = useRef(false);
  const convergenceBatchInFlightRef = useRef(false);
  const liveConfigDriftBatchInFlightRef = useRef(false);

  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());
  const [bulkOpsBusy, setBulkOpsBusy] = useState(false);
  const [bulkOpsProgress, setBulkOpsProgress] = useState<{ current: number; total: number; device_id: string } | null>(null);
  const [bulkOpsResults, setBulkOpsResults] = useState<{ device_id: string; status: string; error?: string }[]>([]);
  const [bulkOpsDialogOpen, setBulkOpsDialogOpen] = useState(false);

  const [decommissionRedirectTarget, setDecommissionRedirectTarget] = useState<Device | null>(null);
  
  const availablePipelines = [
    'detection_pose_ppe',
    'warehouse_offering',
    'detection_yolov8',
    'warehouse_yolov8_posenet_display',
    'retail_yolov8_posenet_display',
  ];
  const [deviceFormData, setDeviceFormData] = useState<DeviceFormData>({
    device_id: '',
    name: '',
    description: '',
    device_type: 'camera',
    location: '',
    area_id: '',
    group_id: '',
    capabilities: {},
    device_metadata: {},
  });
  const [groupFormData, setGroupFormData] = useState<GroupFormData>({
    name: '',
    description: '',
    location: '',
    tags: {},
  });

  const deviceTypes = [
    { value: 'camera', label: CONFIG_SCOPE_LABELS.cameraStream, icon: '📷' },
    { value: 'sensor', label: 'Sensor', icon: '📡' },
    { value: 'gateway', label: 'Gateway', icon: '🌐' },
    { value: 'edge', label: CONFIG_SCOPE_LABELS.edgeDevice, icon: '💻' },
  ];

  const deviceStatuses = {
    online: { label: 'Online', color: 'success', icon: <WifiIcon /> },
    offline: { label: 'Offline', color: 'default', icon: <WifiOffIcon /> },
    error: { label: 'Error', color: 'error', icon: <ErrorIcon /> },
    maintenance: { label: 'Maintenance', color: 'warning', icon: <BuildIcon /> },
  };

  const fetchData = useCallback(async () => {
    try {
      if (!orgId || !siteId) {
        setDevices([]);
        setGroups([]);
        setSites([]);
        setError('Org/site scope is not set yet. Re-login or choose scope from the header.');
        setDevicesLoaded(true);
        setLoading(false);
        return;
      }
      setLoading(true);
      setDevicesLoaded(false);
      const gen = ++devicesListFetchGenRef.current;
      const params = { org_id: orgId, site_id: siteId };
      const [devicesResponse, groupsResponse, scopedSites] = await Promise.all([
        withTimeout(api.get(endpoints.devices.list, { params, timeout: PAGE_TIMEOUT_MS }), 'Devices'),
        withTimeout(api.get(endpoints.devices.groups, { params, timeout: PAGE_TIMEOUT_MS }), 'Device groups'),
        withTimeout(sitesApi.listSites(orgId), 'Sites').catch(() => []),
      ]);
      const baseDevices: Device[] = devicesResponse.data.devices || [];
      setDevices(baseDevices);
      setGroups(groupsResponse.data.groups || []);
      setSites(scopedSites);

      scheduleDeferredWork(() => {
        if (gen !== devicesListFetchGenRef.current) return;
        void (async () => {
          try {
            const ids = baseDevices.map((d) => d.device_id).filter(Boolean);
            if (ids.length) {
              const st = await withTimeout(
                api.post('/api/v1/provisioning/devices/status_batch', { device_ids: ids, org_id: orgId, site_id: siteId }, { timeout: PAGE_TIMEOUT_MS }),
                'Provisioning status'
              );
              if (gen !== devicesListFetchGenRef.current) return;
              const items: any[] = st.data?.items || [];
              setDevices(
                mergeProvisioningStatusIntoDevices(baseDevices as any[], items, { org_id: orgId, site_id: siteId }) as Device[]
              );
            }
          } catch {
            if (gen === devicesListFetchGenRef.current) setDevices(baseDevices);
          }
        })();
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || err?.message || 'Failed to fetch data');
    } finally {
      setDevicesLoaded(true);
      setLoading(false);
    }
  }, [orgId, siteId]);

  useEffect(() => {
    // Only fetch data when authentication is complete and user is logged in
    if (!authLoading && user) {
      void fetchData();
    }
  }, [authLoading, user, fetchData]);

  const fetchRegistrySummary = async (deviceId: string) => {
    if (!deviceId || registrySummaryInFlightRef.current.has(deviceId)) return;
    registrySummaryInFlightRef.current.add(deviceId);
    try {
      // Suppress noisy 409s for demo/virtual devices: if there is no device IP,
      // the /pipelines proxy cannot reach an edge health API. Skip that call.
      const d = (devices || []).find((x) => String(x?.device_id) === String(deviceId));
      const hasIp = !!(d && d.ip_address);
      const pipesReq = hasIp
        ? api.get(`/api/v1/registry/devices/${deviceId}/pipelines`)
        : Promise.resolve({ data: { cameras: [], running_count: 0 } } as any);

      const [cams, pipes, status] = await Promise.all([
        api.get(`/api/v1/registry/devices/${deviceId}/cameras`),
        pipesReq,
        api.get(`/api/v1/registry/devices/${deviceId}/status`),
      ]);
      const cameras_count = (cams.data?.items || []).length;
      const pipelines_total = (pipes.data?.cameras || []).length || cameras_count;
      const pipelines_running = Number(pipes.data?.running_count || 0);
      setRegistrySummaries((prev) => ({
        ...prev,
        [deviceId]: {
          cameras_count,
          pipelines_running,
          pipelines_total,
          in_sync: !!status.data?.in_sync,
          edge_reachable: !!status.data?.edge_reachable,
          updated_at_ms: Date.now(),
          stale: false,
          error: undefined,
          error_at_ms: undefined,
        },
      }));
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to load registry summary';
      setRegistrySummaries((prev) => {
        const existing = prev[deviceId];
        // Preserve last-good values; mark as stale (retrying).
        if (existing && typeof existing.cameras_count === 'number') {
          return {
            ...prev,
            [deviceId]: {
              ...existing,
              updated_at_ms: Date.now(),
              stale: true,
              error: String(msg),
              error_at_ms: Date.now(),
            },
          };
        }
        return {
          ...prev,
          [deviceId]: {
            cameras_count: 0,
            pipelines_running: 0,
            pipelines_total: 0,
            updated_at_ms: Date.now(),
            stale: true,
            error: String(msg),
            error_at_ms: Date.now(),
          },
        };
      });
    } finally {
      registrySummaryInFlightRef.current.delete(deviceId);
    }
  };

  useEffect(() => {
    if (activeTab !== 0 || !devices.length) return;
    const visible = (devices || []).filter((d) => {
      if (siteFilter === 'all') return true;
      return String(d?.site_id || 'default') === siteFilter;
    });
    const top = visible.slice(0, 6).map((d) => d.device_id).filter(Boolean);
    const kickoff = window.setTimeout(() => {
      top.forEach((id) => {
        const existing = registrySummaries[id];
        if (!existing || Date.now() - existing.updated_at_ms > 30_000) {
          void fetchRegistrySummary(id);
        }
      });
    }, 250);
    return () => window.clearTimeout(kickoff);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, devices, siteFilter]);

  const fetchDiagnosisBatch = async (deviceIds: string[]) => {
    const ids = (deviceIds || []).filter(Boolean);
    if (!ids.length || diagnosisBatchInFlightRef.current) return;
    diagnosisBatchInFlightRef.current = true;
    setDiagnosisLoading(true);
    try {
      const res = await api.post('/api/v1/pipeline/devices/diagnosis_batch', {
        device_ids: ids,
        // Keep list snappy via server-side caching, but allow enough time for edge /health/stages
        // (it may run a brief MQTT probe).
        max_age_sec: 10,
        edge_timeout_sec: 8,
      });
      const items: any[] = res.data?.items || [];
      const next: Record<string, any> = {};
      items.forEach((x) => {
        if (x?.device_id) next[String(x.device_id)] = x;
      });
      setDiagnosisSummaries((prev) => ({ ...prev, ...next }));
      setDiagnosisUpdatedAtMs(Date.now());
    } catch {
      // best-effort: ignore (device list remains usable)
    } finally {
      diagnosisBatchInFlightRef.current = false;
      setDiagnosisLoading(false);
    }
  };

  const fetchConvergenceBatch = async (deviceIds: string[]) => {
    const ids = (deviceIds || []).filter(Boolean);
    if (!ids.length || convergenceBatchInFlightRef.current) return;
    convergenceBatchInFlightRef.current = true;
    setConvergenceLoading(true);
    try {
      const res = await api.post('/api/v1/deployments/clarity/convergence_batch', {
        device_ids: ids,
        limit: 200,
      });
      const items: any[] = res.data?.items || [];
      const next: Record<string, any> = {};
      items.forEach((x) => {
        if (x?.device_id) next[String(x.device_id)] = x;
      });
      setConvergenceSummaries((prev) => ({ ...prev, ...next }));
      setConvergenceUpdatedAtMs(Date.now());
    } catch {
      // best-effort: ignore (device list remains usable)
    } finally {
      convergenceBatchInFlightRef.current = false;
      setConvergenceLoading(false);
    }
  };

  const fetchLiveConfigDriftBatch = async (deviceIds: string[]) => {
    const ids = (deviceIds || []).filter(Boolean);
    if (!ids.length || liveConfigDriftBatchInFlightRef.current) return;
    liveConfigDriftBatchInFlightRef.current = true;
    setLiveConfigDriftLoading(true);
    try {
      const next: Record<string, ConfigDriftResponse> = {};
      const chunkSize = 6; // keep browser + backend from spiking
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const settled = await Promise.allSettled(
          chunk.map((id) =>
            getConfigDrift(id, {
              types: ['zones', 'policies', 'lines', 'drb', 'fcp'],
              timeout_ms: 1500,
            })
          )
        );
        settled.forEach((r) => {
          if (r.status === 'fulfilled' && r.value?.device_id) {
            next[String(r.value.device_id)] = r.value;
          }
        });
      }
      setLiveConfigDriftByDevice((prev) => ({ ...prev, ...next }));
      setLiveConfigDriftUpdatedAtMs(Date.now());
    } catch {
      // best-effort: ignore (device list remains usable)
    } finally {
      liveConfigDriftBatchInFlightRef.current = false;
      setLiveConfigDriftLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 0 || !devices.length) return;
    const visible = (devices || []).filter((d) => {
      if (siteFilter === 'all') return true;
      return String(d?.site_id || 'default') === siteFilter;
    });
    const ids = visible.slice(0, 8).map((d) => d.device_id).filter(Boolean);
    const kickoff = window.setTimeout(() => {
      void fetchDiagnosisBatch(ids);
    }, 300);
    const t = diagnosisAutoRefresh
      ? window.setInterval(() => {
          void fetchDiagnosisBatch(ids);
        }, 5000)
      : null;
    return () => {
      window.clearTimeout(kickoff);
      if (t !== null) window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, devices, diagnosisAutoRefresh, siteFilter]);

  useEffect(() => {
    if (activeTab !== 0 || !devices.length) return;
    const visible = (devices || []).filter((d) => {
      if (siteFilter === 'all') return true;
      return String(d?.site_id || 'default') === siteFilter;
    });
    const ids = visible.slice(0, 8).map((d) => d.device_id).filter(Boolean);
    const kickoff = window.setTimeout(() => {
      void fetchLiveConfigDriftBatch(ids);
    }, 700);
    const t = window.setInterval(() => {
      void fetchLiveConfigDriftBatch(ids);
    }, 15000);
    return () => {
      window.clearTimeout(kickoff);
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, devices, siteFilter]);

  useEffect(() => {
    if (activeTab !== 0 || !devices.length) return;
    const visible = (devices || []).filter((d) => {
      if (siteFilter === 'all') return true;
      return String(d?.site_id || 'default') === siteFilter;
    });
    const ids = visible.slice(0, 12).map((d) => d.device_id).filter(Boolean);
    const kickoff = window.setTimeout(() => {
      void fetchConvergenceBatch(ids);
    }, 1000);
    const t = window.setInterval(() => {
      void fetchConvergenceBatch(ids);
    }, 15000);
    return () => {
      window.clearTimeout(kickoff);
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, devices, siteFilter]);

  const stageStatus = (deviceId: string, stage: string): string => {
    const d = diagnosisSummaries[deviceId];
    const locked = d?.stages_locked?.[stage];
    const lockedStatus = String(locked?.status || '').toLowerCase();
    if (lockedStatus) {
      if (lockedStatus === 'healthy') return 'ok';
      if (lockedStatus === 'critical') return 'down';
      if (lockedStatus === 'warn' || lockedStatus === 'warning' || lockedStatus === 'degraded') return 'degraded';
    }

    const canonicalStageMap: Record<string, string> = {
      'edge.policy': 'event_router',
      'edge.evidence_upload': 'upload_sync',
    };
    const canonicalName = canonicalStageMap[String(stage)];
    if (canonicalName) {
      const c = d?.canonical_stages?.[canonicalName];
      const state = String(c?.state || '').toUpperCase();
      if (state === 'OK') return 'ok';
      if (state === 'OFFLINE') return 'down';
      if (state === 'STALLED' || state === 'DEGRADED') return 'degraded';
    }

    const st = d?.stages?.[stage];
    return String(st?.status || d?.stages?.[String(stage)]?.status || 'unknown');
  };

  const chipColorForStatus = (s: string): any => {
    const v = String(s || 'unknown').toLowerCase();
    if (v === 'ok') return 'success';
    if (v === 'degraded') return 'warning';
    if (v === 'down') return 'error';
    return 'default';
  };

  const rankStatus = (s: string): number => {
    const v = String(s || 'unknown').toLowerCase();
    if (v === 'down') return 3;
    if (v === 'degraded') return 2;
    if (v === 'unknown') return 1;
    return 0;
  };

  const worstStatus = (statuses: string[]): string => {
    let worst = 'ok';
    (statuses || []).forEach((s) => {
      if (rankStatus(s) > rankStatus(worst)) worst = String(s || 'unknown');
    });
    return worst;
  };

  const overallRollupStatus = (d: Device): string => {
    const live = deviceLivenessStatus(d);
    const workload = deviceWorkloadStatus(d);
    const diagRow = diagnosisSummaries[d.device_id] || {};
    const overallState = String(diagRow?.overall_state || '').toUpperCase();
    const diag =
      overallState === 'OK'
        ? 'ok'
        : overallState === 'OFFLINE'
          ? 'down'
          : overallState === 'STALLED' || overallState === 'DEGRADED'
            ? 'degraded'
            : String(diagRow?.overall_status || 'unknown');
    // Overall rollup is the worst of transport, detection workload, and diagnosis.
    return worstStatus([live, workload === 'not_configured' ? 'ok' : workload, diag]);
  };

  const configConvergenceStatus = (deviceId: string): string => {
    const live = liveConfigDriftByDevice?.[deviceId];
    const s = String(live?.overall_status || convergenceSummaries?.[deviceId]?.overall_status || 'unknown').toLowerCase();
    if (s === 'converged') return 'ok';
    if (s === 'pending') return 'degraded';
    // DB-only; may be stale. Use warning (orange) not error (red) — Diagnostics has live edge view.
    if (s === 'drifted') return 'degraded';
    return 'unknown';
  };

  /** Phase C: Build type breakdown for Worst drift tooltip (drb, policies, zones, fcp, lines). */
  const worstDriftTooltip = (deviceId: string): string => {
    const live = liveConfigDriftByDevice?.[deviceId];
    if (live?.types && typeof live.types === 'object') {
      const parts: string[] = [];
      const order = ['drb', 'policies', 'zones', 'fcp', 'lines'];
      for (const t of order) {
        const st = String((live.types as any)?.[t]?.status || 'unknown').toLowerCase();
        if (st !== 'unknown') parts.push(`${t}: ${st}`);
      }
      const em = (live as any)?.edge_merge;
      const edgeHint = em
        ? em.succeeded
          ? 'edge: reachable'
          : `edge: ${String(em.error || 'unreachable')}`
        : 'edge: unknown';
      const suffix = `Live (Diagnostics-aligned); ${edgeHint}. Click for details.`;
      return parts.length ? `${parts.join(' • ')} — ${suffix}` : suffix;
    }

    const item = convergenceSummaries?.[deviceId];
    if (!item) return 'DB-only. Click for live edge view in Diagnostics';
    const types = item.types as Record<string, { status?: string }> | undefined;
    if (!types || typeof types !== 'object') return 'DB-only. Click for live edge view in Diagnostics';
    const parts: string[] = [];
    const order = ['drb', 'policies', 'zones', 'fcp', 'lines'];
    for (const t of order) {
      const st = String((types[t]?.status || 'unknown')).toLowerCase();
      if (st !== 'unknown') parts.push(`${t}: ${st}`);
    }
    const suffix = 'DB-only; click for live edge view';
    return parts.length ? `${parts.join(' • ')} — ${suffix}` : suffix;
  };

  const isClaimOk = (r: any): boolean => {
    if (!r) return false;
    if (r?.device_id) return true;
    if (r?.claim_status) return true;
    if (r?.device?.device_id) return true;
    return false;
  };

  const claimDeviceIdFromResult = (r: any): string | null => {
    const did = (r?.device_id || r?.device?.device_id || '').toString().trim();
    return did || null;
  };

  const startProvisionWatch = (deviceId: string) => {
    const did = String(deviceId || '').trim();
    if (!did) return;
    setProvisionWatchDeviceId(did);
    setProvisionWatch({
      device_id: did,
      started_at_ms: Date.now(),
      phase: 'starting',
      provisioning_status: null,
      last_seen: null,
      heartbeat: null,
      heartbeat_age_s: null,
      error: null,
    });
    setProvisionWatchRunning(true);
  };

  const stopProvisionWatch = () => {
    setProvisionWatchRunning(false);
  };

  const handleOpenDeviceDialog = (device?: Device) => {
    const scopedSite = String(device?.site_id || siteId || '').trim();
    if (scopedSite) {
      sitesApi.listAreas(scopedSite)
        .then((areas) => setSiteAreas(Array.isArray(areas) ? areas : []))
        .catch(() => setSiteAreas([]));
    } else {
      setSiteAreas([]);
    }
    if (device) {
      setEditingDevice(device);
      setDeviceFormData({
        device_id: device.device_id,
        name: device.name,
        description: device.description || '',
        device_type: device.device_type,
        location: resolveEdgePlacementDisplay(device),
        area_id: String(device.area_id || '').trim(),
        group_id: device.group_id || '',
        capabilities: device.capabilities || {},
        device_metadata: device.device_metadata || {},
      });
    } else {
      setEditingDevice(null);
      setDeviceFormData({
        device_id: '',
        name: '',
        description: '',
        device_type: 'camera',
        location: '',
        area_id: '',
        group_id: '',
        capabilities: {},
        device_metadata: {},
      });
    }
    setOpenDeviceDialog(true);
  };

  const handleOpenGroupDialog = (group?: DeviceGroup) => {
    if (group) {
      setEditingGroup(group);
      setGroupFormData({
        name: group.name,
        description: group.description || '',
        location: group.location || '',
        tags: group.tags || {},
      });
    } else {
      setEditingGroup(null);
      setGroupFormData({
        name: '',
        description: '',
        location: '',
        tags: {},
      });
    }
    setOpenGroupDialog(true);
  };

  const handleCloseDeviceDialog = () => {
    setOpenDeviceDialog(false);
    setEditingDevice(null);
  };

  const handleCloseGroupDialog = () => {
    setOpenGroupDialog(false);
    setEditingGroup(null);
  };

  const handleDeviceSubmit = async () => {
    try {
      const payload: any = buildDeviceUpdatePayload(deviceFormData, orgId, siteId);
      if (editingDevice) {
        // Canonical placement via sites API; do not send legacy location on device PUT.
        await api.put(endpoints.devices.update(editingDevice.id), payload);
        await saveDevicePlacement({
          canWriteSites,
          siteId: String(editingDevice.site_id || siteId || '').trim(),
          deviceId: editingDevice.device_id,
          areaId: deviceFormData.area_id,
          location: deviceFormData.location,
        });
      } else {
        payload.location = edgeLabelFromForm(deviceFormData.location);
        await api.post(endpoints.devices.create, payload);
      }
      handleCloseDeviceDialog();
      fetchData();
    } catch (err: any) {
      const status = err.response?.status;
      const d = err.response?.data;
      const msg =
        (typeof d?.error === 'string' ? d.error : null) ||
        (typeof d?.detail === 'string' ? d.detail : null) ||
        (typeof d?.msg === 'string' ? d.msg : null) ||
        (Array.isArray(d?.detail) ? d.detail.map((x: any) => x?.msg || x?.message || String(x)).filter(Boolean).join('; ') : null) ||
        (typeof d?.message === 'string' ? d.message : null) ||
        (status === 401 ? 'Session expired or not authenticated. Please log in again.' : null) ||
        (typeof err.message === 'string' && err.message ? err.message : null) ||
        (d && typeof d === 'object' ? JSON.stringify(d).slice(0, 200) : null) ||
        'Failed to save device';
      setError(msg);
    }
  };

  const handleGroupSubmit = async () => {
    try {
      if (!orgId || !siteId) {
        setError('Set org/site scope (top bar or URL ?org=...&site=...) before creating device groups.');
        return;
      }
      const payload: any = {
        ...groupFormData,
        // Tenant scope is required in prod/strict; include it explicitly so API-key flows work
        // even when header-based scope is disabled.
        org_id: orgId,
        site_id: siteId,
      };
      if (editingGroup) {
        await api.put(endpoints.devices.groups + `/${editingGroup.id}`, payload);
      } else {
        await api.post(endpoints.devices.groups, payload);
      }
      handleCloseGroupDialog();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to save group');
    }
  };

  const handleOpenTokenDialog = async (device: Device) => {
    setTokenDevice(device);
    setGeneratedToken(null);
    setTokenDialogOpen(true);
    setGeneratingToken(true);
    
    try {
      const response = await api.post(`/api/v1/provisioning/devices/${device.device_id}/token`);
      setGeneratedToken(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate token');
    } finally {
      setGeneratingToken(false);
    }
  };
  
  const handleCloseTokenDialog = () => {
    setTokenDialogOpen(false);
    setTokenDevice(null);
    setGeneratedToken(null);
    setTokenCopied(false);
    setCommandCopied(false);
    setCopyHint(null);
  };
  
  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    // Modern API (requires HTTPS or localhost). Your dashboard is often served on http://<LAN IP>,
    // so we fall back to an execCommand-based copy for demo/pilot convenience.
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // continue to fallback
    }

    // Fallback: hidden textarea + document.execCommand('copy')
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      // ignore
    }
    return false;
  };

  const handleCopyToken = async () => {
    if (!generatedToken?.token) return;
    setCopyHint(null);
    // Always select the token as a reliable fallback (Ctrl+C works even when clipboard APIs are blocked).
    try {
      tokenTextRef.current?.focus?.();
      tokenTextRef.current?.select?.();
    } catch {
      // ignore
    }
    const ok = await copyTextToClipboard(generatedToken.token);
    if (!ok) {
      setCopyHint('Clipboard write blocked by the browser (common on http:// LAN IP). Token is selected — press Ctrl+C / Cmd+C.');
      return;
    }
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  };

  const buildClaimCurlCommand = (token: { token: string; cloud_url?: string }) => {
    const base = String(token?.cloud_url || window.location.origin).replace(/\/+$/, '');
    // Note: no escaping needed inside a JS template literal.
    return `curl -sS -X POST ${base}/api/v1/provisioning/claim \\\n  -H 'Content-Type: application/json' \\\n  -d '{"token":"${token.token}","device_info":{"hostname":"<edge-host>","ip":"<edge-ip>","capabilities":{}}}'`;
  };

  const handleCopyClaimCommand = async () => {
    if (!generatedToken?.token) return;
    setCopyHint(null);
    const cmd = buildClaimCurlCommand({ token: generatedToken.token, cloud_url: generatedToken.cloud_url });
    const ok = await copyTextToClipboard(cmd);
    if (!ok) {
      setCopyHint('Clipboard write blocked by the browser (common on http:// LAN IP). Please select the command text and press Ctrl+C / Cmd+C.');
      return;
    }
    setCommandCopied(true);
    setTimeout(() => setCommandCopied(false), 2000);
  };

  const handleSimulateClaim = async () => {
    if (!generatedToken?.token) return;
    setClaimingToken(true);
    setClaimResult(null);
    try {
      const res = await api.post('/api/v1/provisioning/claim', {
        token: generatedToken.token,
        device_info: {
          hostname: window.location.hostname || 'web_dashboard',
          capabilities: { cameras: 1 },
          source: 'web_dashboard_simulate',
        },
      });
      setClaimResult(res.data);
      // Refresh device list so status/last_seen reflects activation.
      await fetchData();
      // Watch this device for heartbeat + activation.
      if (tokenDevice?.device_id) startProvisionWatch(tokenDevice.device_id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to claim device');
    } finally {
      setClaimingToken(false);
    }
  };

  const handleOpenClaimDialog = () => {
    setClaimTokenInput('');
    setClaimDialogOpen(true);
  };

  const handleCloseClaimDialog = () => {
    setClaimDialogOpen(false);
    setClaimTokenInput('');
  };

  const handleClaimSubmit = async () => {
    if (!claimTokenInput.trim()) return;
    setClaimLoading(true);
    try {
      const res = await api.post('/api/v1/provisioning/claim', {
        token: claimTokenInput.trim(),
        device_info: {
          hostname: window.location.hostname || 'web_dashboard',
          capabilities: { cameras: 1 },
          source: 'web_dashboard_claim',
        },
      });
      setClaimResult(res.data);
      await fetchData();
      const did = claimDeviceIdFromResult(res.data);
      if (did) startProvisionWatch(did);
      // Keep dialog open long enough to show success; user can close.
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to claim device');
    } finally {
      setClaimLoading(false);
    }
  };

  useEffect(() => {
    if (!provisionWatchRunning || !provisionWatchDeviceId) return;
    let cancelled = false;
    const did = provisionWatchDeviceId;
    const startedAt = Number(provisionWatch?.started_at_ms || Date.now());
    const maxMs = 120_000; // demo SLA: <2 minutes end-to-end

    const tick = async () => {
      if (cancelled) return;
      const now = Date.now();
      if (now - startedAt > maxMs) {
        setProvisionWatch((prev: any) => ({ ...(prev || {}), phase: 'timeout', error: 'Timed out waiting for claim/heartbeat' }));
        setProvisionWatchRunning(false);
        return;
      }

      // 1) Provisioning status (best-effort; requires JWT)
      let provisioning_status: string | null = null;
      let last_seen: string | null = null;
      try {
        const st = await api.get(`/api/v1/provisioning/devices/${encodeURIComponent(did)}/provisioning-status`);
        provisioning_status = String(st.data?.provisioning_status || '');
        last_seen = st.data?.last_seen ? String(st.data.last_seen) : null;
      } catch {
        // ignore
      }

      // 2) Heartbeat (best-effort; does not require JWT)
      let hb: any = null;
      let hb_age_s: number | null = null;
      try {
        const r = await api.get(`/api/v1/devices/${encodeURIComponent(did)}/heartbeat`);
        hb = r.data?.last_seen || null;
        hb_age_s = typeof r.data?.age_s === 'number' ? Number(r.data.age_s) : null;
      } catch {
        hb = null;
        hb_age_s = null;
      }

      // Derive phase
      let phase = 'waiting_for_claim';
      const claimed = String(provisioning_status || '').toLowerCase() === 'activated';
      if (claimed) phase = 'waiting_for_heartbeat';
      if (claimed && typeof hb_age_s === 'number' && hb_age_s <= offlineThresholdSec()) phase = 'done';

      setProvisionWatch((prev: any) => ({
        ...(prev || {}),
        device_id: did,
        provisioning_status: provisioning_status || null,
        last_seen,
        heartbeat: hb,
        heartbeat_age_s: hb_age_s,
        phase,
        error: null,
      }));

      if (phase === 'done') {
        setProvisionWatchRunning(false);
      }
    };

    void tick();
    const t = window.setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provisionWatchRunning, provisionWatchDeviceId]);

  const handleOpenPipelineDialog = (device: Device) => {
    setPipelineDevice(device);
    setPipelineDialogOpen(true);
    setPipelineMessage(null);
    setSelectedPipeline('');
  };

  const handleClosePipelineDialog = () => {
    setPipelineDialogOpen(false);
    setPipelineDevice(null);
    setPipelineMessage(null);
  };

  const handlePipelineAction = async (action: string) => {
    if (!pipelineDevice) return;
    
    setPipelineLoading(true);
    setPipelineMessage(null);
    
    try {
      let response;
      const deviceId = pipelineDevice.device_id;
      
      switch (action) {
        case 'switch':
          if (!selectedPipeline) {
            setPipelineMessage({ type: 'error', text: 'Please select a pipeline' });
            setPipelineLoading(false);
            return;
          }
          response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/switch`, {
            pipeline: selectedPipeline
          });
          break;
        case 'restart':
          response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/restart`);
          break;
        case 'stop':
          response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/stop`);
          break;
        case 'start':
          response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/start`);
          break;
        case 'status':
          response = await api.get(`/api/v1/pipeline/devices/${deviceId}/pipeline/status`);
          break;
        default:
          throw new Error('Unknown action');
      }
      
      setPipelineMessage({ 
        type: 'success', 
        text: response.data.message || `${action} command sent successfully` 
      });
    } catch (err: any) {
      setPipelineMessage({ 
        type: 'error', 
        text: err.response?.data?.error || `Failed to ${action} pipeline` 
      });
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleDeleteDeviceClick = (device: Device) => {
    setDecommissionRedirectTarget(device);
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        const params = orgId && siteId ? { org_id: orgId, site_id: siteId } : undefined;
        await api.delete(endpoints.devices.groups + `/${groupId}`, { params });
        fetchData();
      } catch (err: any) {
        setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to delete group');
      }
    }
  };

  const isDemoVirtualDevice = (d: Device): boolean => {
    const id = String(d?.device_id || '');
    if (id.startsWith('virtual-')) return true;
    return !!(d as any)?.device_metadata?.demo_virtual;
  };

  const setDemoDeviceState = async (d: Device, state: 'healthy' | 'degraded' | 'offline') => {
    try {
      await api.post(`/api/v1/devices/demo/${encodeURIComponent(d.device_id)}/set_state`, { state });
      await fetchData();
      // Best-effort: refresh chip data quickly
      void fetchDiagnosisBatch([d.device_id]);
      // Refresh registry summary to avoid stale “—”
      void fetchRegistrySummary(d.device_id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to set demo state');
    }
  };

  const createDemoVirtualDevice = async (siteId: string) => {
    try {
      await api.post('/api/v1/devices/demo/virtual', { count: 1, site_id: siteId, state: 'healthy' });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create demo device');
    }
  };

  const getDeviceTypeIcon = (type: string) => {
    const deviceType = deviceTypes.find(t => t.value === type);
    return deviceType ? deviceType.icon : '📱';
  };

  const getDeviceTypeLabel = (type: string) => {
    const deviceType = deviceTypes.find(t => t.value === type);
    return deviceType ? deviceType.label : type;
  };

  const getGroupName = (groupId?: string) => {
    if (!groupId) return 'No Group';
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : 'Unknown Group';
  };

  const getSiteDisplay = (device: Device) => (
    resolveDeviceSiteDisplay(device, sites, orgId, siteId)
  );

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          Please log in to view devices
        </Typography>
      </Box>
    );
  }

  const siteOptions = Array.from(
    new Set((devices || []).map((d) => String(d?.site_id || 'default')))
  ).sort();
  const filteredDevices = (devices || []).filter((d) => {
    if (siteFilter === 'all') return true;
    return String(d?.site_id || 'default') === siteFilter;
  });

  const toggleDeviceSelection = (deviceId: string) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };
  const toggleAllFiltered = () => {
    if (selectedDeviceIds.size >= filteredDevices.length) {
      setSelectedDeviceIds(new Set());
    } else {
      setSelectedDeviceIds(new Set(filteredDevices.map((d) => d.device_id)));
    }
  };

  const runBulkConvergenceRecover = async () => {
    const ids = Array.from(selectedDeviceIds);
    if (!ids.length) return;
    const templates = await fetchOpsRunbookTemplates();
    const tpl = (Array.isArray(templates) ? templates : []).find((t: OpsRunbookTemplate) => t.id === 'convergence_recover');
    if (!tpl) {
      setError('Runbook convergence_recover not found');
      return;
    }
    setBulkOpsBusy(true);
    setBulkOpsResults([]);
    setBulkOpsDialogOpen(true);
    const results: { device_id: string; status: string; error?: string }[] = [];
    for (let i = 0; i < ids.length; i++) {
      const did = ids[i];
      setBulkOpsProgress({ current: i + 1, total: ids.length, device_id: did });
      try {
        const ex = await executeRunbookServer(tpl, {
          device_id: did,
          actor: user?.username,
          params: { device_id: did },
        });
        results.push({ device_id: did, status: ex.status || 'succeeded' });
      } catch (e: any) {
        results.push({ device_id: did, status: 'failed', error: e?.response?.data?.error || e?.message || 'Failed' });
      }
      setBulkOpsResults([...results]);
    }
    setBulkOpsProgress(null);
    setBulkOpsBusy(false);
    setSelectedDeviceIds(new Set());
  };

  const openBulkProofPack = () => {
    const ids = Array.from(selectedDeviceIds);
    const first = filteredDevices.find((d) => ids.includes(d.device_id));
    const siteId = first?.site_id || 'default';
    setSelectedDeviceIds(new Set());
    const fallbackPath = buildArtifactFlowPath({
      kind: 'proof_pack',
      siteId,
    });
    void openPersistedProofPack(fallbackPath);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
            Configure
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ mb: 0.25 }}>
            Devices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure edge devices and gateways, then hand off to Diagnostics when a unit needs troubleshooting.
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Open documentation (Devices)">
            <IconButton
              size="small"
              component="a"
              href="/docs/user-guide/configuration/devices/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mr: 1 }}
            >
              <DocsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<WizardIcon />}
            onClick={() => navigate('/ops/runbooks')}
            sx={{ mr: 1 }}
          >
            Ops Runbooks
          </Button>
          <Button
            variant="outlined"
            startIcon={<BugReportIcon />}
            onClick={() => {
              const fallbackPath = buildArtifactFlowPath({
                kind: 'proof_pack',
              });
              void openPersistedProofPack(fallbackPath);
            }}
            sx={{ mr: 1 }}
          >
            Incident evidence package
          </Button>
          <Button
            variant="outlined"
            startIcon={<KeyIcon />}
            onClick={handleOpenClaimDialog}
            sx={{ mr: 1 }}
            disabled={!canWriteDevices}
          >
            Claim Edge Box
          </Button>
          <Button
            variant="outlined"
            startIcon={<WizardIcon />}
            onClick={() => createDemoVirtualDevice(siteFilter === 'all' ? 'default' : siteFilter)}
            sx={{ mr: 1 }}
            disabled={!canWriteDevices}
          >
            Add Virtual
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddGroupIcon />}
            onClick={() => handleOpenGroupDialog()}
            sx={{ mr: 1 }}
            disabled={!canWriteDevices}
          >
            New Group
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDeviceDialog()}
            disabled={!canWriteDevices}
          >
            New Device
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {canReadDevices && !canWriteDevices && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only device access. Device inventory is visible, but create, edit, claim, delete, and control actions are disabled for this account.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Device state shows inventory and provisioning state. Config drift shows whether deployed behavior may differ from the latest
        saved configuration, and live health shows what the unit is reporting right now.
      </Alert>

      {/* Bulk ops bar (Phase 4) */}
      {selectedDeviceIds.size > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.selected', border: '1px solid', borderColor: 'primary.main' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {selectedDeviceIds.size} device(s) selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={bulkOpsBusy ? <CircularProgress size={16} /> : <ConvergenceIcon />}
              disabled={bulkOpsBusy || !canWriteDevices}
              onClick={runBulkConvergenceRecover}
            >
              Run convergence_recover
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ProofPackIcon />}
              disabled={bulkOpsBusy}
              onClick={openBulkProofPack}
            >
              Generate incident evidence package
            </Button>
            <Button variant="text" size="small" onClick={() => setSelectedDeviceIds(new Set())}>
              Clear selection
            </Button>
          </Box>
        </Paper>
      )}

      {/* Device Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {!devicesLoaded && devices.length === 0 ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} md={3} key={i}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width={120} height={28} />
                    <Skeleton variant="text" width={60} height={48} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </>
        ) : (
        <>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Devices
              </Typography>
              <Typography variant="h3" color="primary">
                {filteredDevices.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Operational Devices
              </Typography>
              <Typography variant="h3" color="success.main">
                {filteredDevices.filter(isDeviceOperational).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Error Devices
              </Typography>
              <Typography variant="h3" color="error.main">
                {filteredDevices.filter((d) => overallRollupStatus(d) === 'down').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Device Groups
              </Typography>
              <Typography variant="h3" color="info.main">
                {groups.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        </>
        )}
      </Grid>

      {/* Tabs for Devices and Groups */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Devices (${filteredDevices.length}/${devices.length})`} />
          <Tab label={`Groups (${groups.length})`} />
        </Tabs>

        {/* Devices Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={<Switch checked={diagnosisAutoRefresh} onChange={(e) => setDiagnosisAutoRefresh(e.target.checked)} />}
                  label="Live health (5s)"
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="site-filter-label">Site</InputLabel>
                  <Select
                    labelId="site-filter-label"
                    label="Site"
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(String(e.target.value))}
                  >
                    <MenuItem value="all">All sites</MenuItem>
                    {siteOptions.map((s) => (
                      <MenuItem key={s} value={s}>
                        {sites.find((candidate) => candidate.org_id === orgId && candidate.site_id === s)?.display_name || s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {diagnosisLoading ? 'Refreshing…' : diagnosisUpdatedAtMs ? `Last health update: ${new Date(diagnosisUpdatedAtMs).toLocaleTimeString()}` : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {(liveConfigDriftLoading || convergenceLoading)
                    ? 'Refreshing config drift…'
                    : liveConfigDriftUpdatedAtMs
                      ? `Last config drift update: ${new Date(liveConfigDriftUpdatedAtMs).toLocaleTimeString()}`
                      : convergenceUpdatedAtMs
                        ? `Last config convergence update: ${new Date(convergenceUpdatedAtMs).toLocaleTimeString()}`
                        : '—'}
                </Typography>
              </Box>
            </Box>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedDeviceIds.size > 0 && selectedDeviceIds.size < filteredDevices.length}
                        checked={filteredDevices.length > 0 && selectedDeviceIds.size >= filteredDevices.length}
                        onChange={toggleAllFiltered}
                      />
                    </TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>Edge placement</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Device state</TableCell>
                    <TableCell>Config drift</TableCell>
                    <TableCell>Live health</TableCell>
                    <TableCell>{CONFIG_SCOPE_LABELS.cameraStream}s</TableCell>
                    <TableCell>Pipelines</TableCell>
                    <TableCell>Network</TableCell>
                    <TableCell>Last Seen</TableCell>
                    <TableCell>Config Version</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!devicesLoaded && filteredDevices.length === 0 ? (
                    <>
                      {[0, 1, 2, 3].map((i) => (
                        <TableRow key={i}>
                          <TableCell padding="checkbox"><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
                          <TableCell><Skeleton variant="text" width={120} /></TableCell>
                          <TableCell><Skeleton variant="text" width={60} /></TableCell>
                          <TableCell><Skeleton variant="text" width={60} /></TableCell>
                          <TableCell><Skeleton variant="text" width={80} /></TableCell>
                          <TableCell><Skeleton variant="text" width={70} /></TableCell>
                          <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
                          <TableCell><Skeleton variant="text" width={50} /></TableCell>
                          <TableCell><Skeleton variant="rounded" width={50} height={24} /></TableCell>
                          <TableCell><Skeleton variant="text" width={30} /></TableCell>
                          <TableCell><Skeleton variant="text" width={30} /></TableCell>
                          <TableCell><Skeleton variant="text" width={40} /></TableCell>
                          <TableCell><Skeleton variant="text" width={100} /></TableCell>
                          <TableCell><Skeleton variant="text" width={40} /></TableCell>
                          <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                  <>
                  {filteredDevices.map((device) => (
                    <TableRow key={device.id} hover selected={selectedDeviceIds.has(device.device_id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDeviceIds.has(device.device_id)}
                          onChange={() => toggleDeviceSelection(device.device_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <span style={{ marginRight: '8px' }}>
                            {getDeviceTypeIcon(device.device_type)}
                          </span>
                          <Box>
                            <Typography variant="subtitle2">{device.name}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {device.device_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getDeviceTypeLabel(device.device_type)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <DeviceSiteAreaCell
                          siteLabel={getSiteDisplay(device).label}
                          hasArea={Boolean(String(device.area_name || device.area_id || '').trim())}
                          canSetArea={canWriteSites}
                          onSetArea={() => handleOpenDeviceDialog(device)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{resolveEdgePlacementDisplay(device) || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getGroupName(device.group_id)}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const lifecycle = device.lifecycle_summary;
                          const lifecycleMeta = getDeviceLifecycleMetadata(lifecycle?.state);
                          const lifecycleDetail = [
                            lifecycle?.reason || lifecycleMeta.reason,
                            lifecycle?.deployment_status ? `deploy=${lifecycle.deployment_status}` : '',
                            lifecycle?.post_deploy_status ? `postDeploy=${lifecycle.post_deploy_status}` : '',
                            lifecycle?.decommission_phase ? `decommission=${lifecycle.decommission_phase}` : '',
                          ]
                            .filter(Boolean)
                            .join(' | ');
                          return (
                            <Tooltip title={lifecycleDetail} arrow>
                              <Chip
                                label={lifecycleMeta.label}
                                color={lifecycleMeta.color}
                                size="small"
                                sx={{ mr: 1 }}
                              />
                            </Tooltip>
                          );
                        })()}
                        <Chip
                          icon={deviceStatuses[device.status as keyof typeof deviceStatuses]?.icon}
                          label={deviceStatuses[device.status as keyof typeof deviceStatuses]?.label || device.status}
                          color={deviceStatuses[device.status as keyof typeof deviceStatuses]?.color as any}
                          size="small"
                          variant="outlined"
                        />
                        {(() => {
                          const live = deviceLivenessStatus(device);
                          const age = device.heartbeat_age_s;
                          const connected = String(live).toLowerCase() === 'ok';
                          const label = connected
                            ? 'Edge connected'
                            : typeof age === 'number'
                              ? `Edge offline (${Math.round(age)}s)`
                              : 'Edge offline';
                          return (
                            <Chip
                              label={label}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1 }}
                              color={chipColorForStatus(connected ? 'ok' : 'down')}
                            />
                          );
                        })()}
                        {(() => {
                          const workload = deviceWorkloadStatus(device);
                          const labels: Record<string, string> = {
                            ok: 'Detection running',
                            degraded: 'Detection stopped',
                            down: 'Detection stopped',
                            not_configured: 'Detection not configured',
                            unknown: 'Detection unknown',
                          };
                          const tone =
                            workload === 'not_configured' ? 'unknown' : workload;
                          return (
                            <Chip
                              label={labels[workload] || `Detection ${workload}`}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1 }}
                              color={chipColorForStatus(tone)}
                            />
                          );
                        })()}
                        {device.provisioning_status && (
                          <Chip
                            label={`Provisioning: ${device.provisioning_status}`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                            color={device.provisioning_status === 'activated' ? 'success' : 'warning'}
                          />
                        )}
                        {device.has_pending_token && (
                          <Chip
                            label={`Token pending${device.token_expires_at ? ` (exp ${new Date(device.token_expires_at).toLocaleString()})` : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                            color="warning"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={worstDriftTooltip(device.device_id)} arrow>
                          <Link
                            to={
                              `/diagnostics?device=${encodeURIComponent(device.device_id)}` +
                              (device.site_id ? `&site=${encodeURIComponent(device.site_id)}` : '')
                            }
                            style={{ textDecoration: 'none', display: 'inline-block' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={String(liveConfigDriftByDevice?.[device.device_id]?.overall_status || convergenceSummaries?.[device.device_id]?.overall_status || 'unknown')}
                              color={chipColorForStatus(configConvergenceStatus(device.device_id))}
                              clickable
                            />
                          </Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const did = device.device_id;
                          const overall = overallRollupStatus(device);
                          const pol = stageStatus(did, 'edge.policy');
                          const ev = stageStatus(did, 'edge.evidence_upload');
                          return (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip size="small" label={`Live overall: ${overall}`} color={chipColorForStatus(overall)} variant="outlined" />
                              <Chip size="small" label={`Policy runtime: ${pol}`} color={chipColorForStatus(pol)} variant="outlined" />
                              <Chip size="small" label={`Evidence upload: ${ev}`} color={chipColorForStatus(ev)} variant="outlined" />
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = registrySummaries[device.device_id];
                          if (!s) return <Typography variant="caption" color="text.secondary">—</Typography>;
                          if (s.error) {
                            // Avoid scary “unreachable” flashes: keep last-good value and mark stale.
                            return (
                              <Tooltip title={`Stale (retrying): ${String(s.error)}`} arrow>
                                <Chip
                                  size="small"
                                  label={`${s.cameras_count}`}
                                  variant="outlined"
                                  color="warning"
                                />
                              </Tooltip>
                            );
                          }
                          return (
                            <Chip
                              size="small"
                              label={`${s.cameras_count}`}
                              variant="outlined"
                              color={s.cameras_count > 0 ? 'primary' : 'default'}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = registrySummaries[device.device_id];
                          if (!s) return <Typography variant="caption" color="text.secondary">—</Typography>;
                          if (s.error) {
                            const label = `${s.pipelines_running}/${s.pipelines_total}`;
                            return (
                              <Tooltip title={`Stale (retrying): ${String(s.error)}`} arrow>
                                <Chip size="small" label={label} color="warning" variant="outlined" />
                              </Tooltip>
                            );
                          }
                          const label = `${s.pipelines_running}/${s.pipelines_total}`;
                          const ok = s.pipelines_total > 0 && s.pipelines_running === s.pipelines_total;
                          return (
                            <Chip
                              size="small"
                              label={label}
                              color={ok ? 'success' : 'warning'}
                              variant={ok ? 'filled' : 'outlined'}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Box>
                          {device.ip_address && (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {device.ip_address}
                            </Typography>
                          )}
                          {device.mac_address && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                              {device.mac_address}
                            </Typography>
                          )}
                          {!device.ip_address && !device.mac_address && (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'Never'}
                          </Typography>
                          {device.last_seen && (
                            <Typography variant="caption" color="text.secondary">
                              {(() => {
                                const ageMs = Date.now() - new Date(device.last_seen).getTime();
                                const ageS = Math.max(0, Math.floor(ageMs / 1000));
                                if (ageS < 60) return `${ageS}s ago`;
                                const ageM = Math.floor(ageS / 60);
                                if (ageM < 60) return `${ageM}m ago`;
                                const ageH = Math.floor(ageM / 60);
                                return `${ageH}h ago`;
                              })()}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {device.current_config_version || 'v1.0.0'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Device" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeviceDialog(device)}
                            color="primary"
                            disabled={!canWriteDevices}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {isDemoVirtualDevice(device) && (
                          <>
                            <Tooltip title="Demo: set Healthy" arrow>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setDemoDeviceState(device, 'healthy')}
                                disabled={!canWriteDevices}
                              >
                                <StartIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Demo: set Degraded" arrow>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => setDemoDeviceState(device, 'degraded')}
                                disabled={!canWriteDevices}
                              >
                                <ErrorIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Demo: set Offline" arrow>
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => setDemoDeviceState(device, 'offline')}
                                disabled={!canWriteDevices}
                              >
                                <WifiOffIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Decommission / Factory Reset" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDeviceClick(device)}
                            color="warning"
                            disabled={!canWriteDevices}
                          >
                            <BuildIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Device Details" arrow>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => navigate(`/setup?device=${encodeURIComponent(device.device_id)}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open Setup Wizard (preselected)" arrow>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => navigate(`/setup?device=${encodeURIComponent(device.device_id)}`)}
                          >
                            <WizardIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate Activation Token" arrow>
                          <IconButton 
                            size="small" 
                            color="warning"
                            onClick={() => handleOpenTokenDialog(device)}
                            disabled={!canWriteDevices}
                          >
                            <KeyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Quick Pipeline Control" arrow>
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => handleOpenPipelineDialog(device)}
                            disabled={!canWriteDevices}
                          >
                            <PipelineIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Device Control Center" arrow>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => navigate(`/devices/${device.id}/control`)}
                            disabled={!canWriteDevices}
                          >
                            <ControlPanelIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Diagnostics (device)" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/diagnostics?device=${encodeURIComponent(device.device_id)}`)}
                          >
                            <BugReportIcon />
                          </IconButton>
                        </Tooltip>
                        {grafanaBase && (
                          <Tooltip title="View in Grafana (Pilot Overview)" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              component="a"
                              href={`${grafanaBase}/d/pilot-overview?var-device_id=${encodeURIComponent(device.device_id)}&from=now-6h&to=now`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <LinkIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Firmware & Readiness" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setFirmwareDevice(device);
                              setFirmwareDialogOpen(true);
                            }}
                            disabled={!canWriteDevices}
                          >
                            <BuildIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Groups Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {groups.map((group) => (
                <Grid item xs={12} md={6} key={group.id}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            {group.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" paragraph>
                            {group.description || 'No description'}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            📍 {group.location || 'No location'}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            📱 {group.device_count} devices
                          </Typography>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenGroupDialog(group)}
                            color="primary"
                            disabled={!canWriteDevices}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteGroup(group.id)}
                            color="error"
                            disabled={!canWriteDevices}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Device Dialog */}
      <Dialog open={openDeviceDialog} onClose={handleCloseDeviceDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDevice ? 'Edit Device' : 'New Device'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Device ID"
                value={deviceFormData.device_id}
                onChange={(e) => setDeviceFormData({ ...deviceFormData, device_id: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Device Name"
                value={deviceFormData.name}
                onChange={(e) => setDeviceFormData({ ...deviceFormData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Device Type</InputLabel>
                <Select
                  value={deviceFormData.device_type}
                  onChange={(e) => setDeviceFormData({ ...deviceFormData, device_type: e.target.value })}
                  label="Device Type"
                >
                  {deviceTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Device Group</InputLabel>
                <Select
                  value={deviceFormData.group_id}
                  onChange={(e) => setDeviceFormData({ ...deviceFormData, group_id: e.target.value })}
                  label="Device Group"
                >
                  <MenuItem value="">No Group</MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={deviceFormData.description}
                onChange={(e) => setDeviceFormData({ ...deviceFormData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <DevicePlacementFields
              areaId={deviceFormData.area_id}
              placementLabel={deviceFormData.location}
              areas={siteAreas}
              disabled={Boolean(editingDevice) && !canWriteSites}
              onAreaChange={(areaId) => setDeviceFormData({ ...deviceFormData, area_id: areaId })}
              onPlacementChange={(location) => setDeviceFormData({ ...deviceFormData, location })}
            />
            <Grid item xs={12} md={6}>
              {/*
                Cooling annotation drives which strict commissioning profile
                deploy_edge.sh applies. "active" devices have fans and trip
                the standard thermal.headroom blocker meaningfully; "fanless"
                devices (e.g. .63 / .177 SKUs) idle hot by design and must
                use warehouse_v1_fanless. Default is "active" so unannotated
                devices keep their existing strict semantics.
              */}
              <FormControl fullWidth>
                <InputLabel>Cooling</InputLabel>
                <Select
                  value={(deviceFormData.device_metadata && deviceFormData.device_metadata.cooling) || 'active'}
                  onChange={(e) => setDeviceFormData({
                    ...deviceFormData,
                    device_metadata: {
                      ...(deviceFormData.device_metadata || {}),
                      cooling: e.target.value,
                    },
                  })}
                  label="Cooling"
                >
                  <MenuItem value="active">Active (fan present)</MenuItem>
                  <MenuItem value="fanless">Fanless (skip thermal blocker)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeviceDialog}>Cancel</Button>
          <Button onClick={handleDeviceSubmit} variant="contained" disabled={!canWriteDevices}>
            {editingDevice ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Dialog */}
      <Dialog open={openGroupDialog} onClose={handleCloseGroupDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Edit Device Group' : 'New Device Group'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={groupFormData.location}
                onChange={(e) => setGroupFormData({ ...groupFormData, location: e.target.value })}
                placeholder="e.g., Main Store, Warehouse B"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGroupDialog}>Cancel</Button>
          <Button onClick={handleGroupSubmit} variant="contained" disabled={!canWriteDevices}>
            {editingGroup ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activation Token Dialog */}
      <Dialog open={tokenDialogOpen} onClose={handleCloseTokenDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <KeyIcon color="warning" />
            Device Activation Token
          </Box>
        </DialogTitle>
        <DialogContent>
          {tokenDevice && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Generating token for: <strong>{tokenDevice.name}</strong> ({tokenDevice.device_id})
              </Alert>
              
              {generatingToken ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : generatedToken ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Activation Token:
                  </Typography>
                  <TextField
                    fullWidth
                    inputRef={tokenTextRef}
                    value={generatedToken.token}
                    onFocus={(e) => (e.target as HTMLInputElement | HTMLTextAreaElement).select()}
                    onClick={(e) => (e.target as HTMLInputElement | HTMLTextAreaElement).select()}
                    multiline
                    minRows={2}
                    InputProps={{ readOnly: true }}
                    sx={{
                      mb: 2,
                      '& .MuiInputBase-input': { fontFamily: 'monospace' },
                    }}
                  />
                  
                  <Button
                    variant="outlined"
                    startIcon={<CopyIcon />}
                    onClick={handleCopyToken}
                    color={tokenCopied ? 'success' : 'primary'}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    {tokenCopied ? 'Copied!' : 'Copy Token'}
                  </Button>
                  {copyHint && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {copyHint}
                    </Alert>
                  )}

                  <Button
                    variant="outlined"
                    onClick={() => tokenDevice?.device_id && startProvisionWatch(tokenDevice.device_id)}
                    disabled={!tokenDevice?.device_id}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Watch claim & heartbeat (2 min)
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleSimulateClaim}
                    disabled={claimingToken}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    {claimingToken ? 'Claiming…' : 'Simulate Claim (demo)'}
                  </Button>

                  {isClaimOk(claimResult) && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Claimed device <strong>{claimDeviceIdFromResult(claimResult) || '—'}</strong>
                      {claimResult?.site_id ? (
                        <>
                          {' '}
                          for site <strong>{String(claimResult.site_id)}</strong>
                        </>
                      ) : null}
                    </Alert>
                  )}
                  
                  <Alert severity="warning">
                    <Typography variant="body2">
                      ⏰ {generatedToken.message || 'Token validity'}
                      <br />
                      📅 {new Date(generatedToken.expires_at).toLocaleString()}
                    </Typography>
                  </Alert>

                  {/* Guided provisioning progress */}
                  {provisionWatch?.device_id === tokenDevice.device_id && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Provisioning progress
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip size="small" label="1) token issued" color="success" variant="outlined" />
                        <Chip
                          size="small"
                          label={`2) claimed: ${String(provisionWatch.provisioning_status || 'pending')}`}
                          color={String(provisionWatch.provisioning_status || '').toLowerCase() === 'activated' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={
                            typeof provisionWatch.heartbeat_age_s === 'number'
                              ? `3) heartbeat: ${Math.round(provisionWatch.heartbeat_age_s)}s ago`
                              : '3) heartbeat: not seen yet'
                          }
                          color={
                            typeof provisionWatch.heartbeat_age_s === 'number' && provisionWatch.heartbeat_age_s <= offlineThresholdSec()
                              ? 'success'
                              : 'warning'
                          }
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={String(provisionWatch.phase || '')}
                          color={provisionWatch.phase === 'done' ? 'success' : provisionWatch.phase === 'timeout' ? 'error' : 'default'}
                          variant="outlined"
                        />
                        {provisionWatchRunning && (
                          <Chip size="small" label="polling…" color="info" variant="outlined" />
                        )}
                      </Box>
                      {provisionWatch?.error && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {String(provisionWatch.error)}
                        </Alert>
                      )}
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button size="small" variant="text" onClick={() => tokenDevice?.device_id && startProvisionWatch(tokenDevice.device_id)}>
                          Refresh
                        </Button>
                        <Button size="small" variant="text" onClick={stopProvisionWatch} disabled={!provisionWatchRunning}>
                          Stop
                        </Button>
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      📋 Next Steps:
                    </Typography>
                    <Typography variant="body2" component="div">
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        <li>Copy the token above</li>
                        <li>On the edge device, claim it against the cloud</li>
                        <li>Wait for “heartbeat seen” (this dialog will show it)</li>
                      </ol>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                      Example command (run on edge):
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CopyIcon />}
                          onClick={handleCopyClaimCommand}
                          color={commandCopied ? 'success' : 'primary'}
                        >
                          {commandCopied ? 'Copied!' : 'Copy command'}
                        </Button>
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{ mt: 1, p: 1, bgcolor: 'grey.100', cursor: 'pointer' }}
                        onClick={handleCopyClaimCommand}
                      >
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {buildClaimCurlCommand({ token: generatedToken.token, cloud_url: generatedToken.cloud_url })}
                        </pre>
                      </Paper>
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Alert severity="error">
                  Failed to generate token. Please try again.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTokenDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Claim Edge Box Dialog */}
      <Dialog open={claimDialogOpen} onClose={handleCloseClaimDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <KeyIcon color="primary" />
            Claim Edge Box
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Paste the activation token provided for your edge box. This binds the device to the current org/site.
          </Alert>
          <TextField
            fullWidth
            label="Activation Token"
            value={claimTokenInput}
            onChange={(e) => setClaimTokenInput(e.target.value)}
            placeholder="paste token here"
            multiline
            minRows={3}
            sx={{ mb: 2 }}
          />
          {isClaimOk(claimResult) && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Claimed device <strong>{claimDeviceIdFromResult(claimResult) || '—'}</strong>
              {claimResult?.site_id ? (
                <>
                  {' '}
                  for site <strong>{String(claimResult.site_id)}</strong>
                </>
              ) : null}
            </Alert>
          )}

          {/* Guided provisioning progress (after claim) */}
          {isClaimOk(claimResult) && provisionWatch?.device_id === claimDeviceIdFromResult(claimResult) && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Provisioning progress
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip size="small" label="1) token entered" color="success" variant="outlined" />
                <Chip
                  size="small"
                  label={`2) claimed: ${String(provisionWatch.provisioning_status || 'pending')}`}
                  color={String(provisionWatch.provisioning_status || '').toLowerCase() === 'activated' ? 'success' : 'warning'}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={
                    typeof provisionWatch.heartbeat_age_s === 'number'
                      ? `3) heartbeat: ${Math.round(provisionWatch.heartbeat_age_s)}s ago`
                      : '3) heartbeat: not seen yet'
                  }
                  color={
                    typeof provisionWatch.heartbeat_age_s === 'number' && provisionWatch.heartbeat_age_s <= offlineThresholdSec()
                      ? 'success'
                      : 'warning'
                  }
                  variant="outlined"
                />
                {provisionWatchRunning && <Chip size="small" label="polling…" color="info" variant="outlined" />}
              </Box>
              {provisionWatch?.error && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {String(provisionWatch.error)}
                </Alert>
              )}
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    const did = claimDeviceIdFromResult(claimResult);
                    if (did) startProvisionWatch(did);
                  }}
                >
                  Refresh
                </Button>
                <Button size="small" variant="text" onClick={stopProvisionWatch} disabled={!provisionWatchRunning}>
                  Stop
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClaimDialog}>Close</Button>
          <Button
            onClick={handleClaimSubmit}
            variant="contained"
            disabled={!canWriteDevices || claimLoading || !claimTokenInput.trim()}
          >
            {claimLoading ? 'Claiming…' : 'Claim'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Firmware & Readiness Dialog */}
      <Dialog
        open={firmwareDialogOpen}
        onClose={() => {
          setFirmwareDialogOpen(false);
          setFirmwareDevice(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon color="primary" />
            Firmware & Readiness
          </Box>
          {firmwareDevice && (
            <Typography variant="subtitle2" color="text.secondary">
              Device: {firmwareDevice.name} ({firmwareDevice.device_id})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {firmwareDevice?.device_id ? (
            <FirmwareReadinessPanel deviceId={firmwareDevice.device_id} ipAddress={firmwareDevice.ip_address} />
          ) : (
            <Alert severity="warning">No device selected.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFirmwareDialogOpen(false);
              setFirmwareDevice(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk ops progress dialog (Phase 4) */}
      <Dialog open={bulkOpsDialogOpen} onClose={() => !bulkOpsBusy && setBulkOpsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk run convergence_recover</DialogTitle>
        <DialogContent>
          {bulkOpsProgress && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Running on device {bulkOpsProgress.current} of {bulkOpsProgress.total}: {bulkOpsProgress.device_id}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <CircularProgress size={24} />
              </Box>
            </Box>
          )}
          {bulkOpsResults.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Results
              </Typography>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Run status</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkOpsResults.map((r) => (
                    <TableRow key={r.device_id}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.device_id}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.status}
                          color={r.status === 'succeeded' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="error">
                          {r.error || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpsDialogOpen(false)} disabled={bulkOpsBusy}>
            {bulkOpsBusy ? 'Running…' : 'Close'}
          </Button>
          {!bulkOpsBusy && bulkOpsResults.length > 0 && (
            <Button variant="contained" onClick={() => navigate('/ops/runbooks')}>
              View executions
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Pipeline Control Dialog */}
      <Dialog 
        open={pipelineDialogOpen} 
        onClose={handleClosePipelineDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PipelineIcon color="secondary" />
            Pipeline Control
          </Box>
          {pipelineDevice && (
            <Typography variant="subtitle2" color="text.secondary">
              Device: {pipelineDevice.name} ({pipelineDevice.device_id})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {pipelineMessage && (
            <Alert severity={pipelineMessage.type} sx={{ mb: 2 }}>
              {pipelineMessage.text}
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<StartIcon />}
                onClick={() => handlePipelineAction('start')}
                disabled={!canWriteDevices || pipelineLoading}
              >
                Start
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={() => handlePipelineAction('stop')}
                disabled={!canWriteDevices || pipelineLoading}
              >
                Stop
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<RestartIcon />}
                onClick={() => handlePipelineAction('restart')}
                disabled={!canWriteDevices || pipelineLoading}
              >
                Restart
              </Button>
              <Button
                variant="outlined"
                color="info"
                startIcon={<ViewIcon />}
                onClick={() => handlePipelineAction('status')}
                disabled={pipelineLoading}
              >
                Get Status
              </Button>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Switch Pipeline
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Pipeline</InputLabel>
              <Select
                value={selectedPipeline}
                label="Select Pipeline"
                onChange={(e) => setSelectedPipeline(e.target.value)}
                disabled={!canWriteDevices || pipelineLoading}
              >
                {availablePipelines.map((pipeline) => (
                  <MenuItem key={pipeline} value={pipeline}>
                    {pipeline}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => handlePipelineAction('switch')}
              disabled={!canWriteDevices || pipelineLoading || !selectedPipeline}
              startIcon={pipelineLoading ? <CircularProgress size={20} /> : <PipelineIcon />}
            >
              Switch Pipeline
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              💡 Commands are sent via MQTT. The edge device must be running the 
              Pipeline Control Service to receive commands.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePipelineDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Epic 12 Phase C: Guarded delete device */}
      <Dialog
        open={Boolean(decommissionRedirectTarget)}
        onClose={() => setDecommissionRedirectTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Decommission Device First</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Device removal from `Devices` is disabled. Use decommission from `Bring Up` so the cloud
            record, edge identity, credentials, and optional factory reset stay in sync.
          </Typography>
          <PlaybookCallout
            title="Forced decommission path"
            severity="warning"
            compact
            summary="This page will not perform cloud-only delete. Use Bring Up Danger Zone for decommission and factory reset."
            doItems={[
              'Open Bring Up for this device.',
              'Use Decommission Device and enable Factory reset edge when you are reusing the unit.',
            ]}
            dontItems={[
              'Do not delete a device directly from Devices.',
            ]}
            href={buildDocsUrl('training/EPIC12_IT_ADMIN_TRAINING.md', 'device-removal-and-decommission')}
            linkLabel="Open IT admin playbook"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecommissionRedirectTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              const target = decommissionRedirectTarget;
              setDecommissionRedirectTarget(null);
              if (target?.device_id) {
                navigate(`/bringup?device=${encodeURIComponent(target.device_id)}`);
              }
            }}
          >
            Open Bring Up
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Devices;
